import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "components/Icon/Icon";
import { Popover } from "components/Popover/Popover";
import badgeStyle from "./Badges.module.scss";

/**
 * StateBadges — single-row state-badge strip for table name cells.
 *
 * The state badges (In trash / Read-only / Bundle content / Bootstrap user /
 * deprecation / syntax-error / pending-* / execution mode) used to render in a
 * `flex-wrap: wrap` block below the record name inside the name cell. Once the
 * uniform fixed row-height clip landed
 * (operator-task-fe-list-view-row-height-harmonize-real), a wrapped second badge
 * line was cropped at the bottom by the clip (Margot, screenshot-1445): badges
 * silently disappeared.
 *
 * This lays the badges out on a SINGLE row within the fixed height (never a
 * wrapped second line, so the uniform row height holds) and — when there are more
 * badges than fit the cell width — collapses the overflow into a quiet disclosure
 * that opens a popover listing every badge. That is the same "reveal, never hide"
 * contract already used for overflowing string cells (expand popover) and the
 * actions column (single row, no hidden control): no badge is ever hidden without
 * a way to reveal it. Disclosure styling follows ui-design-principle-show-more
 * (quiet dark-grey → black on hover, no pill/border) and -color-palette (semantic
 * tokens only).
 */
export const StateBadges = ({ children }: { children: ReactNode }) => {
    const stripRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const measure = useCallback(() => {
        const el = stripRef.current;
        if (!el) {
            return;
        }
        // scrollWidth exceeding clientWidth means at least one badge is clipped by
        // the single-row strip and must be reachable via the disclosure.
        setIsOverflowing(el.scrollWidth - el.clientWidth > 1);
    }, []);

    useEffect(() => {
        measure();
        const el = stripRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return;
        }
        // Re-measure when the column/cell is resized (auto table layout) so the
        // disclosure appears/disappears as the available width changes.
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [measure, children]);

    return (
        <div className={badgeStyle.stateBadges}>
            <div ref={stripRef} className={badgeStyle.stateBadgesStrip}>
                {children}
            </div>
            {isOverflowing && (
                <Popover
                    trigger={
                        <span
                            className={badgeStyle.stateBadgesMore}
                            aria-label="Show all badges"
                            title="Show all badges"
                        >
                            <Icon
                                byName="dotsHorizontal"
                                className={badgeStyle.stateBadgesMoreIcon}
                            />
                        </span>
                    }
                    toggleClassName={badgeStyle.stateBadgesMoreToggle}
                    closeOnInsideClick={false}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    contentClassName={badgeStyle.stateBadgesPopover}
                >
                    <div className={badgeStyle.stateBadgesPopoverBody}>{children}</div>
                </Popover>
            )}
        </div>
    );
};
